import { useEffect, useMemo, useRef, useState } from 'react';
import { CreditCard, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { getChoiceOptions } from '@/lib/form-builder/field-validation';
import { AppointmentPickerPreview } from '@/components/forms/AppointmentPickerPreview';
import { RichTextRenderer } from '@/preview/RichTextRenderer';
import type { FormDefinition, FormField } from '@/types/form-definition';
import { cn } from '@/lib/utils';

type PreviewMode = 'preview' | 'respond';

function showLabel(field: FormField) {
  return field.questionProperties?.general?.labelVisible !== false;
}

function qpGeneral(field: FormField): Record<string, unknown> {
  const g = field.questionProperties?.general;
  return g && typeof g === 'object' && !Array.isArray(g) ? (g as Record<string, unknown>) : {};
}

function fieldIsRequired(field: FormField) {
  return Boolean(field.questionProperties?.general?.required);
}

function FieldLabel({
  field,
  required,
  className,
}: {
  field: FormField;
  required: boolean;
  className?: string;
}) {
  if (!showLabel(field)) return null;
  const text = field.label?.trim() || field.type;
  return (
    <Label className={cn('text-sm font-medium', className)}>
      {text}
      {required ? (
        <span className="ml-0.5 text-destructive" aria-hidden>
          *
        </span>
      ) : null}
    </Label>
  );
}

function Hint({ field }: { field: FormField }) {
  const h = field.hint?.trim();
  if (!h) return null;
  return <p className="text-xs text-muted-foreground">{h}</p>;
}

function fieldPlaceholder(field: FormField): string | undefined {
  const ph = field.placeholder?.trim();
  return ph || undefined;
}

/** Clicks on real form controls must not hit the canvas “select field” handler (it used preventDefault and broke radios/selects). */
function isInteractivePreviewTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return Boolean(
    target.closest(
      'input, textarea, button, select, option, label, a, [role="radio"], [role="checkbox"], [role="combobox"], [role="listbox"], [role="switch"], [data-radix-collection-item], [data-slot="select-trigger"], [data-slot="popover-trigger"]'
    )
  );
}

function getSubmitLabel(def: FormDefinition, submitField: FormField): string {
  const fromSettings = def.settings && typeof def.settings === 'object' && !Array.isArray(def.settings)
    ? (def.settings as Record<string, unknown>).submitButtonLabel
    : undefined;
  if (typeof fromSettings === 'string' && fromSettings.trim()) return fromSettings.trim();
  return submitField.label?.trim() || 'Submit';
}

type Answers = Record<string, unknown>;

type LogicAction = 'show' | 'hide' | 'require' | 'unrequire';
type ClauseOp = 'eq' | 'in';
type ConditionClause = { fieldId: string; op: ClauseOp; value?: string; values?: string[] };
type ConditionalLogicBlock = { action: LogicAction; match?: 'all' | 'any'; when?: ConditionClause[] };

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function coerceLogicBlocks(arr: unknown): ConditionalLogicBlock[] {
  const raw = Array.isArray(arr) ? arr : [];
  return raw
    .map((b) => (isPlainObject(b) ? (b as Record<string, unknown>) : null))
    .filter((b): b is Record<string, unknown> => Boolean(b))
    .map((b) => {
      const action = typeof b.action === 'string' ? (b.action as LogicAction) : 'show';
      const match = b.match === 'any' ? 'any' : 'all';
      const whenRaw = Array.isArray(b.when) ? (b.when as unknown[]) : [];
      const when = whenRaw
        .map((w) => (isPlainObject(w) ? (w as Record<string, unknown>) : null))
        .filter((w): w is Record<string, unknown> => Boolean(w))
        .map((w) => {
          const fieldId = typeof w.fieldId === 'string' ? w.fieldId : '';
          const op = w.op === 'in' ? 'in' : 'eq';
          const value = typeof w.value === 'string' ? w.value : undefined;
          const values = Array.isArray(w.values) ? (w.values as unknown[]).map(String) : undefined;
          return { fieldId, op, value, values } satisfies ConditionClause;
        });
      return { action, match, when } satisfies ConditionalLogicBlock;
    })
    .filter((b) => b.action === 'show' || b.action === 'hide' || b.action === 'require' || b.action === 'unrequire');
}

function clauseMatches(clause: ConditionClause, answers: Answers): boolean {
  const v = answers[clause.fieldId];
  if (clause.op === 'eq') {
    return typeof clause.value === 'string' ? String(v ?? '') === clause.value : false;
  }
  if (clause.op === 'in') {
    const values = Array.isArray(clause.values) ? clause.values : [];
    return values.length > 0 ? values.includes(String(v ?? '')) : false;
  }
  return false;
}

/** Rows still being edited (no value / option yet) must not make the whole rule fail under AND. */
function clauseIsConfigured(clause: ConditionClause): boolean {
  if (!clause.fieldId) return false;
  if (clause.op === 'eq') return typeof clause.value === 'string';
  if (clause.op === 'in') {
    const values = Array.isArray(clause.values) ? clause.values : [];
    return values.length > 0;
  }
  return false;
}

function blockMatches(block: ConditionalLogicBlock, answers: Answers): boolean {
  const when = Array.isArray(block.when) ? block.when : [];
  const active = when.filter(clauseIsConfigured);
  if (active.length === 0) return false;
  const match = block.match === 'any' ? 'any' : 'all';
  return match === 'any' ? active.some((c) => clauseMatches(c, answers)) : active.every((c) => clauseMatches(c, answers));
}

function evaluateFieldVisibility(blocks: ConditionalLogicBlock[], answers: Answers): boolean {
  const showBlocks = blocks.filter((b) => b.action === 'show');
  const hideBlocks = blocks.filter((b) => b.action === 'hide');

  let visible = true;
  if (showBlocks.length > 0) {
    visible = showBlocks.some((b) => blockMatches(b, answers));
  }
  if (hideBlocks.some((b) => blockMatches(b, answers))) {
    visible = false;
  }
  return visible;
}

function evaluateFieldRequired(field: FormField, blocks: ConditionalLogicBlock[], answers: Answers, visible: boolean): boolean {
  if (!visible) return false;
  let required = fieldIsRequired(field);
  for (const b of blocks) {
    if (!blockMatches(b, answers)) continue;
    if (b.action === 'require') required = true;
    if (b.action === 'unrequire') required = false;
  }
  return required;
}

export function FormPreview({
  definition,
  mode = 'preview',
  selectedId,
  onSelectFieldId,
}: {
  definition: FormDefinition;
  mode?: PreviewMode;
  selectedId?: string | null;
  onSelectFieldId?: (fieldId: string) => void;
}) {
  const fields = definition.fields ?? [];
  const disabled = mode === 'preview';
  const [answers, setAnswers] = useState<Answers>({});

  const logicByFieldId = useMemo(() => {
    const m = new Map<string, ConditionalLogicBlock[]>();
    for (const f of fields) {
      if (!f.id) continue;
      m.set(f.id, coerceLogicBlocks(f.logic));
    }
    return m;
  }, [fields]);

  const visibleById = useMemo(() => {
    const m = new Map<string, boolean>();
    for (const f of fields) {
      if (!f.id) continue;
      const blocks = logicByFieldId.get(f.id) ?? [];
      m.set(f.id, evaluateFieldVisibility(blocks, answers));
    }
    return m;
  }, [fields, answers, logicByFieldId]);

  const requiredById = useMemo(() => {
    const m = new Map<string, boolean>();
    for (const f of fields) {
      if (!f.id) continue;
      const visible = visibleById.get(f.id) !== false;
      const blocks = logicByFieldId.get(f.id) ?? [];
      m.set(f.id, evaluateFieldRequired(f, blocks, answers, visible));
    }
    return m;
  }, [fields, answers, visibleById, logicByFieldId]);

  const prevVisibleRef = useRef<Map<string, boolean>>(new Map());
  useEffect(() => {
    const prev = prevVisibleRef.current;
    const next = visibleById;
    const toClear: string[] = [];
    for (const [id, nowVisible] of next.entries()) {
      const wasVisible = prev.get(id) !== false;
      if (wasVisible && nowVisible === false) toClear.push(id);
    }
    if (toClear.length > 0) {
      setAnswers((cur) => {
        const out = { ...cur };
        for (const id of toClear) delete out[id];
        return out;
      });
    }
    prevVisibleRef.current = new Map(next);
  }, [visibleById]);

  return (
    <div className="space-y-5">
      {definition.title ? (
        <div className="space-y-1">
          <h2 className="text-xl font-semibold tracking-tight">{definition.title}</h2>
          {definition.description ? (
            <p className="text-sm text-muted-foreground">{definition.description}</p>
          ) : null}
        </div>
      ) : null}

      <div className="space-y-4">
        {fields.map((field) => {
          const g = qpGeneral(field);
          const widthStyle =
            typeof g.width === 'string' && g.width.trim().length > 0 ? { width: g.width.trim() } : undefined;
          const heightStyle =
            typeof g.height === 'string' && g.height.trim().length > 0 ? { height: g.height.trim() } : undefined;
          const containerStyle = { ...(widthStyle ?? {}), ...(heightStyle ?? {}) };
          const hideField =
            field.questionProperties?.advanced &&
            typeof field.questionProperties.advanced === 'object' &&
            !Array.isArray(field.questionProperties.advanced) &&
            (field.questionProperties.advanced as Record<string, unknown>).hideField === true;

          if (hideField) return null;
          if (field.id && visibleById.get(field.id) === false) return null;

          const ph = fieldPlaceholder(field);
          const labelCls = 'mb-1.5 block';
          const required = field.id ? requiredById.get(field.id) === true : fieldIsRequired(field);

          const canSelect = typeof onSelectFieldId === 'function' && Boolean(field.id);
          const isSelected = Boolean(selectedId && field.id && selectedId === field.id);
          const Wrap = ({ children }: { children: React.ReactNode }) => (
            <div
              data-field-id={field.id}
              className={cn(
                canSelect &&
                  'rounded-xl outline-none ring-offset-background transition hover:bg-accent/30 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                isSelected && 'ring-2 ring-primary/40 bg-primary/5'
              )}
              style={containerStyle}
              role={canSelect ? 'button' : undefined}
              tabIndex={canSelect ? 0 : undefined}
              onClick={
                canSelect
                  ? (e) => {
                      if (isInteractivePreviewTarget(e.target)) {
                        return;
                      }
                      e.preventDefault();
                      e.stopPropagation();
                      onSelectFieldId(field.id);
                    }
                  : undefined
              }
              onKeyDown={
                canSelect
                  ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onSelectFieldId(field.id);
                      }
                    }
                  : undefined
              }
            >
              <div className={cn(canSelect && 'p-2')}>{children}</div>
            </div>
          );

          switch (field.type) {
            case 'header':
              return (
                <Wrap key={field.id}>
                  <div className="space-y-1">
                    <h3 className="text-lg font-semibold tracking-tight">{field.label?.trim() || 'Heading'}</h3>
                    {field.config && typeof field.config === 'object' && !Array.isArray(field.config) ? (
                      typeof (field.config as Record<string, unknown>).subHeader === 'string' ? (
                        <p className="text-sm text-muted-foreground">
                          {String((field.config as Record<string, unknown>).subHeader)}
                        </p>
                      ) : null
                    ) : null}
                  </div>
                </Wrap>
              );

            case 'paragraph': {
              const text = (field.html ?? '')
                .replace(/<[^>]+>/g, ' ')
                .replace(/\s+/g, ' ')
                .trim();
              return (
                <Wrap key={field.id}>
                  <p className="text-sm leading-relaxed text-muted-foreground">{text || 'Paragraph text'}</p>
                </Wrap>
              );
            }

            case 'richText': {
              const html = typeof field.html === 'string' ? field.html : '';
              return (
                <Wrap key={field.id}>
                  <RichTextRenderer
                    content={html}
                    fallback={<p className="text-sm text-muted-foreground">Rich content</p>}
                  />
                </Wrap>
              );
            }

            case 'divider':
              return (
                <Wrap key={field.id}>
                  <Separator className="my-2" />
                </Wrap>
              );

            case 'pageBreak':
              return (
                <Wrap key={field.id}>
                  <div className="space-y-2">
                    {field.config && typeof field.config === 'object' && !Array.isArray(field.config) ? (
                      <>
                        {typeof (field.config as Record<string, unknown>).pageTitle === 'string' ? (
                          <p className="text-sm font-medium">
                            {String((field.config as Record<string, unknown>).pageTitle)}
                          </p>
                        ) : null}
                        {typeof (field.config as Record<string, unknown>).pageDescription === 'string' ? (
                          <p className="text-sm text-muted-foreground">
                            {String((field.config as Record<string, unknown>).pageDescription)}
                          </p>
                        ) : null}
                      </>
                    ) : null}
                    <div className="flex items-center gap-2 py-2">
                      <div className="h-px flex-1 bg-border" />
                      <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                        Page break
                      </span>
                      <div className="h-px flex-1 bg-border" />
                    </div>
                  </div>
                </Wrap>
              );

            case 'submit':
              return (
                <Wrap key={field.id}>
                  <Button
                    type="button"
                    disabled
                    className={cn(
                      'pointer-events-none max-w-xs sm:w-auto',
                      field.config &&
                        typeof field.config === 'object' &&
                        !Array.isArray(field.config) &&
                        (field.config as Record<string, unknown>).fullWidth === true &&
                        'w-full'
                    )}
                  >
                    {getSubmitLabel(definition, field)}
                  </Button>
                </Wrap>
              );

            case 'shortText':
              return (
                <Wrap key={field.id}>
                  <div className="space-y-1">
                    <FieldLabel field={field} required={required} className={labelCls} />
                    <Input
                      placeholder={ph}
                      disabled={disabled}
                      className="bg-background/80"
                      value={typeof answers[field.id] === 'string' ? (answers[field.id] as string) : ''}
                      onChange={(e) => setAnswers((a) => ({ ...a, [field.id]: e.target.value }))}
                    />
                    <Hint field={field} />
                  </div>
                </Wrap>
              );

            case 'longText':
              return (
                <Wrap key={field.id}>
                  <div className="space-y-1">
                    <FieldLabel field={field} required={required} className={labelCls} />
                    <Textarea
                      placeholder={ph}
                      disabled={disabled}
                      rows={
                        field.config &&
                        typeof field.config === 'object' &&
                        !Array.isArray(field.config) &&
                        typeof (field.config as Record<string, unknown>).rows === 'number'
                          ? Math.max(1, Number((field.config as Record<string, unknown>).rows))
                          : 3
                      }
                      className="resize-none bg-background/80"
                      value={typeof answers[field.id] === 'string' ? (answers[field.id] as string) : ''}
                      onChange={(e) => setAnswers((a) => ({ ...a, [field.id]: e.target.value }))}
                    />
                    <Hint field={field} />
                  </div>
                </Wrap>
              );

            case 'email':
              return (
                <Wrap key={field.id}>
                  <div className="space-y-1">
                    <FieldLabel field={field} required={required} className={labelCls} />
                    <Input
                      type="email"
                      placeholder={ph ?? 'email@example.com'}
                      disabled={disabled}
                      className="bg-background/80"
                    />
                    <Hint field={field} />
                  </div>
                </Wrap>
              );

            case 'phone':
              return (
                <Wrap key={field.id}>
                  <div className="space-y-1">
                    <FieldLabel field={field} required={required} className={labelCls} />
                    <Input
                      type="tel"
                      placeholder={ph ?? '(555) 000-0000'}
                      disabled={disabled}
                      className="bg-background/80"
                    />
                    <Hint field={field} />
                  </div>
                </Wrap>
              );

            case 'number':
              return (
                <Wrap key={field.id}>
                  <div className="space-y-1">
                    <FieldLabel field={field} required={required} className={labelCls} />
                    <Input type="number" placeholder={ph ?? '0'} disabled={disabled} className="bg-background/80" />
                    <Hint field={field} />
                  </div>
                </Wrap>
              );

            case 'datePicker':
              return (
                <Wrap key={field.id}>
                  <div className="space-y-1">
                    <FieldLabel field={field} required={required} className={labelCls} />
                    <Input type="date" disabled={disabled} className="max-w-[220px] bg-background/80" />
                    <Hint field={field} />
                  </div>
                </Wrap>
              );

            case 'time':
              return (
                <Wrap key={field.id}>
                  <div className="space-y-1">
                    <FieldLabel field={field} required={required} className={labelCls} />
                    <Input type="time" disabled={disabled} className="max-w-[160px] bg-background/80" />
                    <Hint field={field} />
                  </div>
                </Wrap>
              );

            case 'dropdown': {
              const opts = getChoiceOptions(field);
              const first = opts.find((o) => !o.disabled)?.value ?? opts[0]?.value ?? '';
              const current = typeof answers[field.id] === 'string' ? (answers[field.id] as string) : '';
              return (
                <Wrap key={field.id}>
                  <div className="space-y-1">
                    <FieldLabel field={field} required={required} className={labelCls} />
                    <Select
                      disabled={disabled}
                      value={(current || first) ? String(current || first) : undefined}
                      onValueChange={(v) => setAnswers((a) => ({ ...a, [field.id]: v }))}
                    >
                      <SelectTrigger className="max-w-md bg-background/80">
                        <SelectValue placeholder="Select…" />
                      </SelectTrigger>
                      <SelectContent>
                        {opts.map((o, i) => (
                          <SelectItem key={`${field.id}-dd-${i}-${o.value}`} value={o.value} disabled={o.disabled}>
                            {o.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Hint field={field} />
                  </div>
                </Wrap>
              );
            }

            case 'singleChoice': {
              const opts = getChoiceOptions(field);
              const first = opts.find((o) => !o.disabled)?.value ?? opts[0]?.value ?? '';
              const current = typeof answers[field.id] === 'string' ? (answers[field.id] as string) : '';
              return (
                <Wrap key={field.id}>
                  <div className="space-y-2">
                    <FieldLabel field={field} required={required} className={labelCls} />
                    <RadioGroup
                      value={(current || first) ? String(current || first) : ''}
                      disabled={disabled}
                      className="gap-2"
                      onValueChange={(v) => setAnswers((a) => ({ ...a, [field.id]: v }))}
                    >
                      {opts.map((o, i) => (
                        <div key={`${field.id}-sc-${i}-${o.value}`} className="flex items-center gap-2">
                          <RadioGroupItem
                            value={o.value}
                            id={`pv-${field.id}-${i}-${o.value}`}
                            disabled={disabled || o.disabled}
                          />
                          <Label
                            htmlFor={`pv-${field.id}-${i}-${o.value}`}
                            className="font-normal text-muted-foreground"
                          >
                            {o.label}
                          </Label>
                        </div>
                      ))}
                    </RadioGroup>
                    <Hint field={field} />
                  </div>
                </Wrap>
              );
            }

            case 'multipleChoice': {
              const opts = getChoiceOptions(field);
              return (
                <Wrap key={field.id}>
                  <div className="space-y-2">
                    <FieldLabel field={field} required={required} className={labelCls} />
                    <div className="space-y-2">
                      {opts.map((o, i) => (
                        <div key={`${field.id}-mc-${i}-${o.value}`} className="flex items-center gap-2">
                          <Checkbox
                            id={`pv-${field.id}-${i}-${o.value}`}
                            disabled={disabled || o.disabled}
                            checked={false}
                          />
                          <Label
                            htmlFor={`pv-${field.id}-${i}-${o.value}`}
                            className="font-normal text-muted-foreground"
                          >
                            {o.label}
                          </Label>
                        </div>
                      ))}
                    </div>
                    <Hint field={field} />
                  </div>
                </Wrap>
              );
            }

            case 'fullName':
              return (
                <Wrap key={field.id}>
                  <div className="space-y-2">
                    <FieldLabel field={field} required={required} className={labelCls} />
                    <div className="grid gap-2 sm:grid-cols-2">
                      <Input placeholder="First name" disabled={disabled} className="bg-background/80" />
                      <Input placeholder="Last name" disabled={disabled} className="bg-background/80" />
                    </div>
                    <Hint field={field} />
                  </div>
                </Wrap>
              );

            case 'address':
              return (
                <Wrap key={field.id}>
                  <div className="space-y-2">
                    <FieldLabel field={field} required={required} className={labelCls} />
                    <Input placeholder="Street address" disabled={disabled} className="bg-background/80" />
                    <div className="grid gap-2 sm:grid-cols-2">
                      <Input placeholder="City" disabled={disabled} className="bg-background/80" />
                      <Input placeholder="ZIP" disabled={disabled} className="bg-background/80" />
                    </div>
                    <Hint field={field} />
                  </div>
                </Wrap>
              );

            case 'signature':
              return (
                <Wrap key={field.id}>
                  <div className="space-y-1">
                    <FieldLabel field={field} required={required} className={labelCls} />
                    <div className="flex h-24 max-w-md items-center justify-center rounded-md border border-dashed bg-muted/30 text-sm text-muted-foreground">
                      Sign here
                    </div>
                    <Hint field={field} />
                  </div>
                </Wrap>
              );

            case 'fileUpload':
              return (
                <Wrap key={field.id}>
                  <div className="space-y-1">
                    <FieldLabel field={field} required={required} className={labelCls} />
                    <Button type="button" variant="outline" disabled={disabled} className="gap-2">
                      <Upload className="h-4 w-4" />
                      Choose file
                    </Button>
                    <Hint field={field} />
                  </div>
                </Wrap>
              );

            case 'appointment':
              return (
                <Wrap key={field.id}>
                  <div className="space-y-1">
                    <FieldLabel field={field} required={required} className={labelCls} />
                    <AppointmentPickerPreview field={field} className="max-w-3xl" />
                    <Hint field={field} />
                  </div>
                </Wrap>
              );

            case 'payment': {
              const cfg = field.config && typeof field.config === 'object' && !Array.isArray(field.config) ? field.config : {};
              const gateway = typeof (cfg as Record<string, unknown>).gateway === 'string' ? (cfg as Record<string, unknown>).gateway : 'square';
              const amount = (cfg as Record<string, unknown>).amount;
              const amountSource =
                typeof (cfg as Record<string, unknown>).amountSource === 'string' ? (cfg as Record<string, unknown>).amountSource : 'fixed';
              const line =
                amountSource === 'productList'
                  ? 'Product list total'
                  : typeof amount === 'number'
                    ? `$${amount.toFixed(2)} USD`
                    : 'Amount';
              return (
                <Wrap key={field.id}>
                  <div className="space-y-2">
                    <FieldLabel field={field} required={required} className={labelCls} />
                    <div className="flex max-w-md items-center gap-2 rounded-lg border border-border/60 bg-muted/15 px-3 py-3 text-sm">
                      <CreditCard className="h-5 w-5 shrink-0 text-muted-foreground" />
                      <div>
                        <p className="font-medium capitalize">{String(gateway)}</p>
                        <p className="text-xs text-muted-foreground">{line}</p>
                      </div>
                    </div>
                    <p className="text-[11px] text-muted-foreground">
                      Card entry is handled in the student app on submit. Reschedule fee amounts come from the appointment field when applicable.
                    </p>
                    <Hint field={field} />
                  </div>
                </Wrap>
              );
            }

            default:
              return (
                <Wrap key={field.id}>
                  <div className="space-y-1">
                    <FieldLabel field={field} required={required} className={labelCls} />
                    <div className="rounded-md border bg-muted/10 px-3 py-2 text-xs text-muted-foreground">
                      <span className="font-mono">{field.type}</span>
                      {field.label ? <span className="ml-2">{field.label}</span> : null}
                    </div>
                    <Hint field={field} />
                  </div>
                </Wrap>
              );
          }
        })}
      </div>
    </div>
  );
}

